/**
 * Simple cache manager for storing API responses
 * with optional TTL (time-to-live) support
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl?: number; // Time to live in milliseconds
}

class CacheManager {
  private cache: Map<string, CacheEntry<any>>;

  constructor() {
    this.cache = new Map();
  }

  /**
   * Set a value in cache with optional TTL
   * @param key - Cache key
   * @param data - Data to cache
   * @param ttl - Time to live in milliseconds (optional)
   */
  set<T>(key: string, data: T, ttl?: number): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl,
    });
  }

  /**
   * Get a value from cache
   * @param key - Cache key
   * @returns Cached data or null if expired/not found
   */
  get<T>(key: string): T | null {
    const entry = this.cache.get(key);

    if (!entry) {
      return null;
    }

    // Check if entry has expired
    if (entry.ttl) {
      const age = Date.now() - entry.timestamp;
      if (age > entry.ttl) {
        this.cache.delete(key);
        return null;
      }
    }

    return entry.data as T;
  }

  /**
   * Check if a key exists and is valid in cache
   */
  has(key: string): boolean {
    return this.get(key) !== null;
  }

  /**
   * Clear a specific cache entry
   */
  delete(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Clear all cache entries
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Get cache size
   */
  size(): number {
    return this.cache.size;
  }

  /**
   * Clean up expired entries
   */
  cleanup(): void {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (entry.ttl && now - entry.timestamp > entry.ttl) {
        this.cache.delete(key);
      }
    }
  }
}

// Export a singleton instance
export const cache = new CacheManager();

// Cache key generators for consistency
export const CacheKeys = {
  liveCategories: () => "live_categories",
  movieCategories: () => "movie_categories",
  channels: (categoryId: string) => `channels_${categoryId}`,
  movies: (categoryId: string) => `movies_${categoryId}`,
  movieInfo: (movieId: string) => `movie_info_${movieId}`,
  heroMovies: () => "hero_movies",
  homeMovies: () => "home_movies", // NEW: for the Popular Movies row
};

// Default TTL values (in milliseconds)
export const CacheTTL = {
  categories: 30 * 60 * 1000, // 30 minutes
  channels: 15 * 60 * 1000, // 15 minutes
  movies: 15 * 60 * 1000, // 15 minutes
  movieInfo: 60 * 60 * 1000, // 1 hour
  heroMovies: 2 * 60 * 60 * 1000, // 2 hours
  homeMovies: 2 * 60 * 60 * 1000, // 2 hours - NEW
};
